import React, { useState } from 'react';
import { Calculator, Copy, Trash2, DollarSign } from 'lucide-react';

const LoanCalculatorTool = () => {
  const [loanAmount, setLoanAmount] = useState('');
  const [interestRate, setInterestRate] = useState('');
  const [loanTerm, setLoanTerm] = useState('');
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const calculate = () => {
    const principal = parseFloat(loanAmount);
    const annualRate = parseFloat(interestRate);
    const years = parseFloat(loanTerm);

    if (isNaN(principal) || isNaN(annualRate) || isNaN(years) || principal <= 0 || years <= 0) {
      setResult(null);
      return;
    }

    // Standard loan amortization formula
    // M = P * [r(1+r)^n] / [(1+r)^n - 1]
    // where:
    // M = monthly payment
    // P = principal (loan amount)
    // r = monthly interest rate (annual rate / 12 / 100)
    // n = total number of payments (years * 12)

    const monthlyRate = annualRate / 100 / 12;
    const totalPayments = years * 12;

    let monthlyPayment;
    if (monthlyRate === 0) {
      // No interest - simple division
      monthlyPayment = principal / totalPayments;
    } else {
      monthlyPayment = principal * (monthlyRate * Math.pow(1 + monthlyRate, totalPayments)) / (Math.pow(1 + monthlyRate, totalPayments) - 1);
    }

    const totalPayment = monthlyPayment * totalPayments;
    const totalInterest = totalPayment - principal;

    setResult({
      monthlyPayment,
      totalPayment,
      totalInterest,
      principal,
      annualRate,
      years,
      totalPayments
    });
  };

  const copyResult = (value) => {
    navigator.clipboard.writeText(value.toFixed(2));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const clearAll = () => {
    setLoanAmount('');
    setInterestRate('');
    setLoanTerm('');
    setResult(null);
  };

  const formatCurrency = (value) => {
    return '$' + value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  return (
    <div className="space-y-6">
      <div className="p-4 bg-gray-800 rounded-lg space-y-4">
        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Loan Amount ($)
            </label>
            <input
              type="number"
              value={loanAmount}
              onChange={(e) => setLoanAmount(e.target.value)}
              placeholder="e.g., 250000"
              className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none"
              data-testid="loan-amount-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Interest Rate (% per year)
            </label>
            <input
              type="number"
              step="0.01"
              value={interestRate}
              onChange={(e) => setInterestRate(e.target.value)}
              placeholder="e.g., 6.5"
              className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none"
              data-testid="interest-rate-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Loan Term (years)
            </label>
            <input
              type="number"
              value={loanTerm}
              onChange={(e) => setLoanTerm(e.target.value)}
              placeholder="e.g., 30"
              className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none"
              data-testid="loan-term-input"
            />
          </div>
        </div>
      </div>

      <button
        onClick={calculate}
        className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium"
        data-testid="calculate-loan-btn"
      >
        <Calculator size={18} />
        Calculate Loan
      </button>

      {result && (
        <div className="p-4 bg-gray-800 rounded-lg space-y-4" data-testid="loan-results">
          <div className="grid md:grid-cols-3 gap-4">
            <div className="p-4 bg-gray-900 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-400">Monthly Payment</span>
                <button
                  onClick={() => copyResult(result.monthlyPayment)}
                  className={`p-1.5 rounded transition-colors ${
                    copied ? 'bg-green-600' : 'bg-gray-700 hover:bg-gray-600'
                  }`}
                  data-testid="copy-monthly-payment"
                >
                  <Copy size={14} />
                </button>
              </div>
              <div className="text-2xl font-bold text-green-400" data-testid="monthly-payment-result">
                {formatCurrency(result.monthlyPayment)}
              </div>
            </div>

            <div className="p-4 bg-gray-900 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-400">Total Payment</span>
                <button
                  onClick={() => copyResult(result.totalPayment)}
                  className={`p-1.5 rounded transition-colors ${
                    copied ? 'bg-green-600' : 'bg-gray-700 hover:bg-gray-600'
                  }`}
                  data-testid="copy-total-payment"
                >
                  <Copy size={14} />
                </button>
              </div>
              <div className="text-2xl font-bold text-blue-400" data-testid="total-payment-result">
                {formatCurrency(result.totalPayment)}
              </div>
            </div>

            <div className="p-4 bg-gray-900 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-400">Total Interest</span>
                <button
                  onClick={() => copyResult(result.totalInterest)}
                  className={`p-1.5 rounded transition-colors ${
                    copied ? 'bg-green-600' : 'bg-gray-700 hover:bg-gray-600'
                  }`}
                  data-testid="copy-total-interest"
                >
                  <Copy size={14} />
                </button>
              </div>
              <div className="text-2xl font-bold text-yellow-400" data-testid="total-interest-result">
                {formatCurrency(result.totalInterest)}
              </div>
            </div>
          </div>

          <div className="border-t border-gray-700 pt-4">
            <h4 className="text-sm font-medium text-gray-300 mb-3">Loan Summary</h4>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Loan Amount</span>
                <span className="text-white font-medium">{formatCurrency(result.principal)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Interest Rate</span>
                <span className="text-white font-medium">{result.annualRate}% per year</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Loan Term</span>
                <span className="text-white font-medium">{result.years} years ({result.totalPayments} payments)</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Interest-to-Principal Ratio</span>
                <span className="text-white font-medium">{((result.totalInterest / result.principal) * 100).toFixed(1)}%</span>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="p-4 bg-gray-800/50 rounded-lg">
        <h3 className="text-sm font-medium text-gray-300 mb-3">Common Loan Types</h3>
        <div className="grid md:grid-cols-3 gap-4 text-sm text-gray-400">
          <div>
            <div className="text-gray-500 mb-1">Mortgage (30-year)</div>
            <div>5.5% - 7.5% APR</div>
          </div>
          <div>
            <div className="text-gray-500 mb-1">Auto Loan</div>
            <div>4% - 10% APR</div>
          </div>
          <div>
            <div className="text-gray-500 mb-1">Personal Loan</div>
            <div>6% - 36% APR</div>
          </div>
        </div>
      </div>

      <div className="p-4 bg-gray-800/50 rounded-lg">
        <h3 className="text-sm font-medium text-gray-300 mb-2">Amortization Formula</h3>
        <div className="text-sm text-gray-400 font-mono bg-gray-900 p-3 rounded">
          M = P × [r(1+r)ⁿ] / [(1+r)ⁿ - 1]
        </div>
        <div className="text-xs text-gray-500 mt-2">
          Where M = monthly payment, P = principal, r = monthly rate, n = total payments
        </div>
      </div>

      <button
        onClick={clearAll}
        className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
        data-testid="clear-loan-btn"
      >
        <Trash2 size={16} />
        Clear All
      </button>
    </div>
  );
};

export default LoanCalculatorTool;
